import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, shareReplay } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { API } from '@app-core/constants/api.constants';
import { ClientConfigResponse, setParams } from '@app-core/models/core.model';
import { FleetBrandingApiResponse, TagBrandingConfig } from '@app-core/models/branding.model';
import { applyClientColorsFonts, applyManifest, getClientConfig, setClientConfig, setClientIndex } from '@config/client-config';

@Injectable({
  providedIn: 'root',
})
export class BrandingService {
  private originalConfig: any;
  private tagBrandingConfig: TagBrandingConfig | null = null;
  private preloadedLogos: Set<string> = new Set();

  private _currentBranding$ = new BehaviorSubject<TagBrandingConfig | null>(null);
  public currentBranding$ = this._currentBranding$.asObservable();

  private _brandingResolved$ = new BehaviorSubject<boolean>(false);
  public brandingResolved$ = this._brandingResolved$.asObservable();

  constructor(private http: HttpClient) {
    this.initializeBaseConfig();
  }

  private initializeBaseConfig(): void {
    const raw = JSON.parse(JSON.stringify(getClientConfig() || {}));
    this.originalConfig = this.deepFreeze(raw);
  }

  // re-apply branding at runtime (after fleet switch)
  private reApplyBranding(clientConfig: any): void {
    if (!clientConfig) return;
    setClientIndex(clientConfig);
    applyClientColorsFonts(clientConfig);
    applyManifest(clientConfig);
  }

  private deepFreeze<T>(obj: T): T {
    Object.freeze(obj);

    Object.getOwnPropertyNames(obj).forEach((prop) => {
      const value = (obj as any)[prop];
      if (value !== null && (typeof value === 'object' || typeof value === 'function') && !Object.isFrozen(value)) {
        this.deepFreeze(value);
      }
    });

    return obj;
  }

  private buildFinalConfig(originalBranding: any, tagBranding: any): any {
    if (!tagBranding?.configurations) {
      return originalBranding;
    }

    const branding = tagBranding.configurations;

    return {
      ...originalBranding,

      title: tagBranding.title ?? originalBranding.title,

      themeColor: tagBranding.themeColor ?? branding.lightPrimary ?? branding.lightAccent ?? originalBranding.themeColor,

      configuration: {
        ...originalBranding.configuration,

        lightPrimary: branding.lightPrimary ?? branding.lightAccent ?? originalBranding.configuration.lightPrimary,

        lightPrimaryContrast:
          branding.lightPrimaryContrast ?? branding.lightAccentContrast ?? originalBranding.configuration.lightPrimaryContrast,

        darkPrimary: branding.darkPrimary ?? branding.darkAccent ?? originalBranding.configuration.darkPrimary,

        darkPrimaryContrast:
          branding.darkPrimaryContrast ?? branding.darkAccentContrast ?? originalBranding.configuration.darkPrimaryContrast,

        lightAccent: branding.lightAccent ?? branding.lightPrimary ?? originalBranding.configuration.lightAccent,

        lightAccentContrast:
          branding.lightAccentContrast ?? branding.lightPrimaryContrast ?? originalBranding.configuration.lightAccentContrast,

        darkAccent: branding.darkAccent ?? branding.darkPrimary ?? originalBranding.configuration.darkAccent,

        darkAccentContrast:
          branding.darkAccentContrast ?? branding.darkPrimaryContrast ?? originalBranding.configuration.darkAccentContrast,

        pageBackground:
          branding.pageBackground ??
          (branding.lightPrimary && `${branding.lightPrimary}2f`) ??
          (branding.lightAccent && `${branding.lightAccent}2f`) ??
          originalBranding.configuration.pageBackground,

        logo: {
          ...originalBranding.configuration.logo,
          ...branding.logo,
        },
      },
    };
  }

  // get fallback logos from the original "/client-ui-config" (tsp branding) response
  // used when /v2/reseller-tag-branding (reseller-tag branding) logos fail to load
  private getFallbackLogos() {
    return this.originalConfig?.configuration?.logo || null;
  }

  // get a specific fallback logo URL by type and theme
  public getFallbackLogoUrl(logoType: 'main' | 'partner', theme: 'light' | 'dark'): string | null {
    const logo = this.getFallbackLogos();
    if (!logo) return null;

    if (logoType === 'main') {
      return theme === 'light' ? logo?.lightLogo : logo?.darkLogo;
    }

    return null;
  }

  /**
   * Get fallback logo with complete metadata including dimensions
   * Used when reseller-tag branding logos fail to load
   */
  public getFallbackLogoMetadata(
    logoType: 'main' | 'partner',
    theme: 'light' | 'dark',
    isMobile: boolean = false
  ): { url: string | null; dimensions: { width?: string; height?: string } } | null {
    const originalConfig = this.originalConfig?.configuration;
    if (!originalConfig) return null;

    if (logoType === 'main') {
      const logo = originalConfig.logo;
      if (!logo) return null;

      // Select appropriate logo based on device and theme
      let url: string | null = null;
      let width: string | undefined;
      let height: string | undefined;

      if (isMobile) {
        url = theme === 'light' ? logo.mobileModeLightLogo : logo.mobileModeDarkLogo;
        width = logo.mobileModeWidth;
        height = logo.mobileModeHeight;
      } else {
        url = theme === 'light' ? logo.lightLogo : logo.darkLogo;
        width = logo.width;
        height = logo.height;
      }

      return url ? { url, dimensions: { width, height } } : null;
    }

    if (logoType === 'partner') {
      const partner = originalConfig.partnerLogo;
      if (!partner) return null;

      let url: string | null = null;
      let width: string | undefined;
      let height: string | undefined;

      if (isMobile) {
        url = theme === 'light' ? partner.partnerMobileModeLightLogo : partner.partnerMobileModeDarkLogo;
        width = partner.mobileModeWidth;
        height = partner.mobileModeHeight;
      } else {
        url = theme === 'light' ? partner.partnerLightLogo : partner.partnerDarkLogo;
        width = partner.width;
        height = partner.height;
      }

      return url ? { url, dimensions: { width, height } } : null;
    }

    return null;
  }

  /**
   * Check if current branding is from reseller-tag API
   * Returns true if fleet-specific branding is active
   */
  public isResellerTagBranding(): boolean {
    return Boolean(
      this.tagBrandingConfig?.configurations?.logo &&
        (this.tagBrandingConfig.configurations.logo.lightLogo || this.tagBrandingConfig.configurations.logo.darkLogo)
    );
  }

  // api call to fetch reseller-tag branding based on fleetId.
  private getResellerTagRebranding(fleetId: string): Observable<FleetBrandingApiResponse> {
    const httpOptions = {
      params: setParams({ fleetId }),
    };
    return this.http.get<FleetBrandingApiResponse>(API.GET_FLEET_BRANDING_CONFIG, httpOptions).pipe(
      catchError(() => {
        return of({ success: false, config: null });
      })
    );
  }

  private applyBranding(config: TagBrandingConfig): void {
    try {
      // freeze tagBrandingConfig
      this.tagBrandingConfig = this.deepFreeze(JSON.parse(JSON.stringify(config)));

      // build final config ONLY here
      const finalConfig = this.buildFinalConfig(this.originalConfig, this.tagBrandingConfig);

      // apply globally
      setClientConfig(finalConfig);
      this.reApplyBranding(finalConfig);

      // remove PWA related stuff
      this.removeFaviconAndManifest();

      // notify components
      this._currentBranding$.next(config);
      this._brandingResolved$.next(true);
    } catch {
      this.resetToDefaultBranding();
    }
  }

  // reset to original "/client-ui-config" tsp branding
  private resetToDefaultBranding(): void {
    this.tagBrandingConfig = null;

    // restore original config
    const baseClone = JSON.parse(JSON.stringify(this.originalConfig));
    setClientConfig(baseClone);
    this.reApplyBranding(baseClone);

    this._currentBranding$.next(null);
    this._brandingResolved$.next(true);
  }

  // removes favicon and manifest link elements from document head
  // called when fleet-specific branding is applied (PWA not needed for reseller-tag branding)
  private removeFaviconAndManifest(): void {
    // set favicon to transparent PNG / default when using reseller-tag branding
    const transparentFaviconDataUrl =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAAXNSR0IArs4c6QAAAAZiS0dEAP8A/wD/oL2nkwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAAd0SU1FB9oFFAADATTAuQQAAAAZdEVYdENvbW1lbnQAQ3JlYXRlZCB3aXRoIEdJTVBXgQ4XAAAAEklEQVQ4y2NgGAWjYBSMAggAAAQQAAGFP6pyAAAAAElFTkSuQmCC';

    let faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (faviconLink) {
      faviconLink.href = '/assets/abc.png'; // so favicon show defaulkt icon
    } else {
      faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.href = transparentFaviconDataUrl;
      document.head.appendChild(faviconLink);
    }

    // remove manifest
    const existingManifest = document.querySelector("link[rel='manifest']");
    if (existingManifest) {
      existingManifest.remove();
    }
  }

  public getClientConfig(): Observable<ClientConfigResponse> {
    return this.http.get<ClientConfigResponse>(API.CLIENT_CONFIG).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  // apply branding when fleet changes.
  public async onFleetChange(fleetId: string) {
    try {
      const clientConfigResponse = await this.getClientConfig().toPromise();
      const { featureFlags = [] } = clientConfigResponse.data;
      const resellerTaggingFlag = featureFlags.find((obj) => obj.feature === 'reseller-tagging') ?? null;

      if (resellerTaggingFlag?.enabled) {
        const res = await this.getResellerTagRebranding(fleetId).toPromise();
        if (res?.config && Object.keys(res.config).length) {
          this.applyBranding(res.config);
        } else {
          this.resetToDefaultBranding();
        }
      } else {
        this.resetToDefaultBranding();
      }
    } catch {
      this.resetToDefaultBranding();
    }
  }

  // get origibnal config (tsp branding)
  public getOriginalConfig(): any {
    return this.originalConfig;
  }

  public getTagBrandingConfig(): TagBrandingConfig | null {
    return this.tagBrandingConfig;
  }

  public clearLogoPreloadCache(): void {
    this.preloadedLogos.clear();
  }
}
